import React from 'react';
import { BookOpen, Users, Globe, Target, Rocket, Laptop, Zap } from 'lucide-react';

const AboutUsPage = () => {
    return (
        <div className="bg-brand-bg min-h-screen font-poppins">
            {/* Hero Section */}
            <div className="relative pt-20 pb-24 lg:pt-32 lg:pb-40 overflow-hidden bg-brand-bg">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&q=80&w=2070"
                        alt="Tech Innovation"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/90 via-brand-bg/95 to-brand-bg/80" />
                </div>

                {/* Abstract Background Shapes */}
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[100px] z-0"></div>
                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] z-0"></div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-brand-textLight">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-brand-gold/10 text-brand-gold font-semibold text-sm mb-6 border border-brand-gold/20 uppercase tracking-widest backdrop-blur-sm">
                        Discover AIGenius
                    </span>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                        Redefining Tech <span className="text-brand-gold">Education</span>
                    </h1>
                    <p className="mt-4 max-w-3xl text-lg md:text-xl mx-auto text-brand-textMuted font-light leading-relaxed">
                        We bridge the gap between academic theory and industry reality, crafting the next generation of Cloud, DevOps, and AI pioneers.
                    </p>
                </div>
            </div>

            {/* Features Section overlapping Hero */}
            <div className="relative z-20 -mt-16 max-w-7xl mx-auto px-4 md:px-6 mb-24">
                <div className="bg-brand-card/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-800 p-4 md:p-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 divide-x-0 md:divide-x divide-y md:divide-y-0 divide-gray-800">
                    {[
                        { title: "Expert Faculty", subtitle: "Learn from industry leaders", icon: Users, color: "text-brand-gold" },
                        { title: "Real Projects", subtitle: "Hands-on implementation", icon: Laptop, color: "text-brand-gold" },
                        { title: "Career Support", subtitle: "Guidance & placement", icon: Target, color: "text-brand-gold" },
                        { title: "Modern Tech", subtitle: "Latest industry stacks", icon: Zap, color: "text-brand-gold" }
                    ].map((feature, i) => (
                        <div key={i} className={`text-center py-4 md:py-0 ${i % 2 !== 0 ? 'border-l pl-2 md:pl-0' : 'pr-2 md:pr-0'} md:!border-l-0 ${i === 2 || i === 3 ? 'border-t md:border-t-0' : ''} lg:!border-l lg:first:!border-l-0`}>
                            <div className="flex justify-center mb-2 md:mb-4">
                                <feature.icon className={`w-6 h-6 md:w-8 md:h-8 ${feature.color}`} />
                            </div>
                            <p className="text-sm md:text-xl font-semibold text-brand-textLight mb-1 md:mb-2">{feature.title}</p>
                            <p className="hidden md:block text-xs font-semibold text-brand-textMuted uppercase tracking-wider">{feature.subtitle}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Our Story Section */}
            <div className="py-16 md:py-24 max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="relative">
                        <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-800 transform transition-transform duration-500 hover:scale-[1.02]">
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800"
                                alt="Team collaboration"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                            <div className="absolute bottom-8 left-8 right-8">
                                <p className="text-white text-lg md:text-xl font-normal leading-relaxed">
                                    "Education is not the learning of facts, but the training of the mind to think."
                                </p>
                            </div>
                        </div>
                        {/* Decorative Background for Image */}
                        <div className="absolute -inset-4 bg-gradient-to-r from-brand-gold/20 to-brand-goldHover/20 rounded-[2rem] z-0 -rotate-3 transform-gpu"></div>
                    </div>

                    <div className="lg:pl-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-8 bg-brand-gold"></div>
                            <span className="text-brand-gold font-bold uppercase tracking-widest text-sm">Our Genesis</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-semibold text-brand-textLight mb-6 leading-tight">
                            Born from Industry <span className="text-brand-gold">Necessity</span>
                        </h2>
                        <div className="prose prose-lg text-brand-textMuted">
                            <p className="mb-6 font-medium text-brand-textLight">
                                AIGenius was conceptualized by veterans from top-tier tech firms who consistently observed a glaring skills gap in fresh graduates.
                            </p>
                            <p className="mb-6 leading-relaxed">
                                We recognized that traditional education models were struggling to keep pace with the rapid evolution of Cloud, DevOps, and Data Science. Our response was to create an immersive, project-driven ecosystem that mirrors genuine corporate environments.
                            </p>
                            <p className="leading-relaxed">
                                Today, we don't just teach technology; we simulate the daily challenges of a software engineer, ensuring our graduates enter the workforce not just prepared, but ready to lead.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Core Values Section */}
            <div className="bg-brand-bg0 py-24 border-y border-gray-800">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl font-semibold text-brand-textLight mb-4">Our DNA</h2>
                        <p className="text-xl text-brand-textMuted">The fundamental principles that guide our curriculum, our community, and our future.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                title: "Practical Mastery",
                                desc: "Theory is important, but execution is everything. We prioritize hands-on labs, real AWS/Azure environments, and production-level deployments.",
                                icon: BookOpen,
                                color: "bg-brand-gold/10 text-brand-gold ring-brand-gold/20"
                            },
                            {
                                title: "Global Perspective",
                                desc: "Technology knows no borders. Our curriculum is designed to meet international standards, preparing students for remote opportunities worldwide.",
                                icon: Globe,
                                color: "bg-brand-gold/10 text-brand-gold ring-brand-gold/20"
                            },
                            {
                                title: "Community Driven",
                                desc: "Learning in isolation limits potential. We foster a vibrant network of peers and mentors who collaborate, review code, and build together.",
                                icon: Users,
                                color: "bg-brand-gold/10 text-brand-gold ring-brand-gold/20"
                            }
                        ].map((value, idx) => (
                            <div key={idx} className="group p-8 bg-brand-card rounded-2xl hover:bg-brand-bg hover:shadow-xl transition-all duration-300 border border-gray-800 hover:border-brand-gold/30 text-center md:text-left">
                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ring-4 ${value.color} transition-transform group-hover:scale-110 mx-auto md:mx-0`}>
                                    <value.icon className="w-7 h-7" />
                                </div>
                                <h3 className="text-xl font-semibold text-brand-textLight mb-3">{value.title}</h3>
                                <p className="text-brand-textMuted leading-relaxed text-sm md:text-base">{value.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-24 bg-brand-bg overflow-hidden border-t border-gray-800">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2070')] opacity-5 bg-cover bg-center"></div>
                <div className="absolute inset-0 bg-brand-bg/50 mix-blend-multiply"></div>
                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-semibold text-brand-textLight mb-6">Ready to Transform Your Career?</h2>
                    <p className="text-lg text-brand-textMuted mb-10 max-w-2xl mx-auto font-light">
                        Join the ranks of AIGenius alumni who are now engineering the future at top technology companies globally.
                    </p>
                    <a href="/contact-us" className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-black bg-brand-gold rounded-xl shadow-lg hover:bg-brand-goldHover hover:scale-105 transition-all duration-300">
                        Get in Touch
                        <Rocket className="ml-2 w-5 h-5 text-black" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AboutUsPage;
